import React from 'react';
import LeverageQuizPage, { GENERAL_CONFIG } from './LeverageQuizPage';

const G = GENERAL_CONFIG;

const LABELS = {
  career: {
    growing: "i've built a solid practice & i'm still growing",
    figuring_out: "i'm still figuring out what i want my practice to be",
  },
  work: { love_it: "i love it. i'm not looking to stop practicing." },
  more_money: {
    more_hours: "i see more patients or work longer days",
    raise_rates: "i raise my rates or look for a better-paying position",
  },
  gift: {
    connect: "connecting people with the right person or resource",
    mentor: "teaching patients & helping them understand what's going on",
    share: "sharing what's worked & getting people excited about it",
  },
  considered: { tried: "i've tried something like that (fullscript, a dispensary, an affiliate link)" },
  want: {
    another_way: "another way to earn that isn't tied directly to my appointments",
    different: "the freedom to make my practice look different down the road",
  },
};

const QUESTION_TEXT = {
  career: "how would you describe where you are in your practice?",
  considered: "have you ever considered adding something where your recommendations could create another way to earn?",
};

const questions = G.questions.map((q) => ({
  ...q,
  question: QUESTION_TEXT[q.id] || q.question,
  answers: q.answers.map((a) => ({ ...a, label: LABELS[q.id]?.[a.value] || a.label })),
}));

const VIDEO_BASE = 'https://www.youtube-nocookie.com/embed/YnSgb9toClU';
const clip = (start, end) => `${VIDEO_BASE}?start=${start}&end=${end}&rel=0&modestbranding=1&playsinline=1`;

const videos = {
  comp: {
    src: clip(271, 313),
    title: "how you'd get paid",
    phrase: "how you'd get paid",
    caption: "40 sec · from my 12-minute breakdown · income isn't guaranteed.",
    captionLink: G.videos.comp.captionLink,
  },
  day_to_day: {
    src: clip(318, 351),
    title: 'what this looks like inside your practice',
    phrase: 'what this looks like inside your practice',
    caption: '30 sec · from my 12-minute breakdown',
  },
  support: {
    src: clip(510, 552),
    title: "who you'd be working with",
    phrase: "who you'd be working with",
    caption: '40 sec · from my 12-minute breakdown',
  },
  company: {
    src: clip(382, 431),
    title: 'the company & what it sells',
    phrase: 'the company & what it sells',
    caption: '50 sec · from my 12-minute breakdown',
  },
};

const HOW = {
  comp: "my 12-minute breakdown & the real numbers from monat's income disclosure, plus what it costs to start (around $300).",
  day_to_day: "my 12-minute breakdown, plus what this looks like inside a real clinic.",
  support: "my 12-minute breakdown, plus who's on my team & what you get when you start.",
  company: "my 12-minute breakdown, plus what the products are & who they're for.",
  all: "the whole thing on one page: my 12-minute breakdown, a real clinic, the real numbers & what it costs to start (around $300).",
};

const TALK = {
  comp: "book a free call & i'll walk you through how the compensation plan works.",
  day_to_day: "book a free call & i'll tell you what this would actually look like with your patients.",
  support: "book a free call & i'll show you the training, the team & the group chat that answers in about five minutes 😁",
  company: "book a free call & i'll tell you about the company & what it sells.",
  all: "book a free call & we'll figure out if it fits your practice. no pitch, just a conversation.",
};

const cardDescriptions = Object.fromEntries(
  Object.entries(G.cardDescriptions).map(([k, d]) => [k, { ...d, howItWorks: HOW[k], talk: TALK[k] }])
);

const TITLE = 'could your practice be making you more money? · kristen hinman';
const DESCRIPTION = '8 quick questions & my honest take on whether this makes sense for your practice right now.';

export const PRACTITIONER_CONFIG = {
  ...G,
  version: 'practitioners',
  storageKey: 'practitionerQuizState',
  meta: { title: TITLE, description: DESCRIPTION },
  intro: {
    eyebrow: 'a quiz for practitioners ✦',
    headlinePrefix: 'could your practice be',
    byline: "hi, i'm kristen. i've spent 15+ years working behind the scenes with doctors & practitioners, and most of them are great at what they do & still only get paid when they're in the room.",
    paragraph: "this quiz is my honest take on whether adding a way to get paid that isn't tied to your appointments makes sense for you. (not everyone should, & i'd rather tell you now.)",
  },
  questions,
  videos,
  allVideosLine: {
    text: 'watch the 12-minute breakdown →',
    url: 'https://practice.kristenhinman.com/?utm_source=practitioner_quiz&utm_content=full_breakdown',
  },
  giftPhrases: {
    connect: 'you connect people with what they need',
    mentor: "you teach your patients & help them understand what's going on",
    share: "you get people excited about what's worked",
    all: 'you do a little of all three: connecting, teaching & sharing',
  },
  figuringOutWhy: (giftPhrase) => `you're still figuring out what you want your practice to be, & ${giftPhrase}. i'd get to know the products first & see how they feel to you.`,
  careerGrowing: "you've built a solid practice",
  loseIncome: ' and right now, your income depends on you being in the room.',
  teamLine: "and you don't have to give up your practice for this, it gets to be both. you don't need to build more, you need to get paid more for what you've already built, so i'll hand you everything i use.",
  wantLines: {
    ...G.wantLines,
    another_way: { ...G.wantLines.another_way, text: "another way to earn that isn't tied to your appointments" },
    different: { ...G.wantLines.different, text: 'the freedom to make your practice look different down the road' },
  },
  cardDescriptions,
  wantAdditions: {
    another_way: ' same reason dr. michelle on my team started.',
    different: ' same reason dr. michelle on my team started.',
  },
  talkCard: {
    buttonText: 'book a free 30-minute call',
    link: 'https://calendly.com/kristenhinman/leverage?utm_source=practitioner_quiz&utm_content=talk',
    ctaType: 'book_call',
    newTab: true,
  },
  productsButtonText: 'find your hair routine',
  allVideosIntro: "before you decide anything, i'd want you to see all of it, so here's me explaining each piece.",
  cardOrders: { ...G.cardOrders, closer_look: ['howItWorks', 'talk', 'products'] },
  howItWorksTitle: 'let me show you how it works inside a practice',
  links: {
    howItWorks: 'https://practice.kristenhinman.com/?utm_source=practitioner_quiz&utm_content=how',
    tryProducts: 'https://leveragequiz.com/try-products?utm_source=practitioner_quiz&utm_content=products',
  },
  share: {
    title: 'could your practice be making you more money?',
    text: DESCRIPTION,
    url: 'https://leveragequiz.com/practitioners',
  },
  finePrintExtra: ' monat products are cosmetics and are not intended to diagnose, treat, cure or prevent any disease.',
};

export default function PractitionerQuizPage() {
  return <LeverageQuizPage config={PRACTITIONER_CONFIG} />;
}
